import apiClient from "./axios"

// ============================
// 🔹 Money Receipt API Services
// ============================

// Get all money receipts (paginated or filtered)
export const getMoneyReceipts = async (params = {}) => {
  const { data } = await apiClient.get("/money-receipts", { params })
  return data
}

// Get a specific money receipt by ID
export const getMoneyReceipt = async id => {
  const { data } = await apiClient.get(`/money-receipts/${id}`)
  return data
}

export const getMoneyReceiptLoan = async id => {
  const { data } = await apiClient.get(`/money-receipts/${id}/loan`)
  return data
}

export const getMoneyReceiptBlueprint = async id => {
  const { data } = await apiClient.get(`/money-receipts/${id}/custom-receipt-blueprint`)
  return data.data
}

// Create a new money receipt
export const createMoneyReceipt = async moneyReceiptData => {
  const { data } = await apiClient.post("/money-receipts", moneyReceiptData)
  return data
}

// Update an existing money receipt
export const editMoneyReceipt = async ({ id, ...moneyReceiptData }) => {
  const { data } = await apiClient.patch(
    `/money-receipts/${id}`,
    moneyReceiptData
  )
  return data
}

// Soft delete a money receipt
export const deleteMoneyReceipt = async id => {
  const resolvedId = typeof id === "object" ? id?.id : id
  const { data } = await apiClient.delete(`/money-receipts/${resolvedId}`)
  return data
}

// Create money receipt from quotation
export const createMoneyReceiptFromQuotation = async quotationData => {
  const { data } = await apiClient.post(
    "/money-receipts/from-quotation",
    quotationData
  )
  return data
}

// Cancel a money receipt
export const cancelMoneyReceipt = async id => {
  const { data } = await apiClient.post(`/money-receipts/${id}/cancel`)
  return data
}

export const saveMoneyReceiptLoan = async ({ id, loan_information }) => {
  const { data } = await apiClient.patch(`/money-receipts/${id}/loan`, {
    loan_information,
  })
  return data
}

export const deleteMoneyReceiptLoan = async id => {
  const { data } = await apiClient.delete(`/money-receipts/${id}/loan`)
  return data
}

// Get money receipts by customer
export const getMoneyReceiptsByCustomer = async (customerId, params = {}) => {
  const { data } = await apiClient.get(
    `/money-receipts/customer/${customerId}`,
    { params }
  )
  return data
}

// Get money receipt statistics
export const getMoneyReceiptStatistics = async () => {
  const { data } = await apiClient.get("/money-receipts/statistics")
  return data
}

// Search money receipts by MR number, customer, or vehicle
export const searchMoneyReceipts = async (searchTerm, params = {}) => {
  const { data } = await apiClient.get("/money-receipts", {
    params: { ...params, search: searchTerm },
  })
  return data
}

// Get all money receipts without pagination (for selectors)
export const getAllMoneyReceipts = async (params = {}) => {
  const { data } = await apiClient.get("/money-receipts", {
    params: { ...params, all: true },
  })
  return data
}

// Filter money receipts by status
export const getMoneyReceiptsByStatus = async (status, params = {}) => {
  const { data } = await apiClient.get("/money-receipts", {
    params: { ...params, status },
  })
  return data
}

// Filter money receipts by payment status
export const getMoneyReceiptsByPaymentStatus = async (
  paymentStatus,
  params = {}
) => {
  const { data } = await apiClient.get("/money-receipts", {
    params: { ...params, payment_status: paymentStatus },
  })
  return data
}

// Filter money receipts by date range
export const getMoneyReceiptsByDateRange = async (
  startDate,
  endDate,
  params = {}
) => {
  const { data } = await apiClient.get("/money-receipts", {
    params: {
      ...params,
      start_date: startDate,
      end_date: endDate,
    },
  })
  return data
}

// Get money receipts for dashboard
export const getMoneyReceiptsDashboard = async () => {
  const { data } = await apiClient.get("/money-receipts/statistics")
  return data
}

// ============================
// 🔹 Payment-related API Services (Account Customer Payments)
// ============================

// Add payment to money receipt
export const addPaymentToMoneyReceipt = async paymentData => {
  const { data } = await apiClient.post(
    "/account-customer-payments",
    paymentData
  )
  return data
}

// Get payments for money receipt
export const getMoneyReceiptPayments = async (moneyReceiptId, params = {}) => {
  const { data } = await apiClient.get("/account-customer-payments", {
    params: { ...params, money_receipt_id: moneyReceiptId },
  })
  return data
}

// Remove payment from money receipt
export const removePaymentFromMoneyReceipt = async paymentId => {
  const { data } = await apiClient.delete(
    `/account-customer-payments/${paymentId}`
  )
  return data
}

// ============================
// 🔹 Helper Functions
// ============================

// Format money receipt number
export const formatMRNumber = mrNumber => {
  if (!mrNumber) return ""
  // Format MR260401001 as MR-26-04-01001
  if (mrNumber.length >= 10) {
    return `${mrNumber.substr(0, 2)}-${mrNumber.substr(2, 2)}-${mrNumber.substr(
      4,
      2
    )}-${mrNumber.substr(6)}`
  }
  return mrNumber
}

// Calculate payment status
export const calculatePaymentStatus = (totalAmount, amountPaid) => {
  if (amountPaid === 0) return "pending"
  if (amountPaid < totalAmount) return "partially_paid"
  return "fully_paid"
}

// Format currency for display
export const formatCurrency = amount => {
  if (typeof amount !== "number") return "0"
  return new Intl.NumberFormat("en-BD", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// Get payment status color
export const getPaymentStatusColor = status => {
  switch (status) {
    case "pending":
      return "warning"
    case "partially_paid":
      return "info"
    case "fully_paid":
      return "success"
    default:
      return "secondary"
  }
}

// Get payment status label
export const getPaymentStatusLabel = status => {
  switch (status) {
    case "pending":
      return "Pending"
    case "partially_paid":
      return "Partially Paid"
    case "fully_paid":
      return "Fully Paid"
    default:
      return "Unknown"
  }
}
